import React, { useCallback } from 'react';
import { useState, useEffect } from 'react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import {
    Grid,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Typography,
    Container,
    Card,
    CardContent,
    Chip,
    Avatar,
    Alert,
    CircularProgress,
    Divider
} from '@mui/material';
import { createTheme, ThemeProvider } from "@mui/material/styles";
import {
    Wifi as WifiIcon,
    Search as SearchIcon,
    FilterList as FilterIcon,
    Download as DownloadIcon,
    LocationOn as LocationIcon,
    Business as BusinessIcon,
    Phone as PhoneIcon,
    Person as PersonIcon,
    Home as HomeIcon,
    Router as RouterIcon,
    QrCode as QrCodeIcon
} from '@mui/icons-material';
import DownloadTable from './download';
import { headerData } from '../../../data/headerCostum';
import DeleteData from './delete';
import DownloadRounded from '@mui/icons-material/DownloadRounded';
const defaultTheme = createTheme({
    palette: {
        primary: {
            main: '#1dc39f',
        },
        secondary: {
            main: '#8ff9ab',
        },
        background: {
            default: '#f8fffe',
        },
    },
    typography: {
        h4: {
            fontWeight: 700,
        },
        h5: {
            fontWeight: 600,
        },
        h6: {
            fontWeight: 600,
        },
    },
    components: {
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 12,
                        backgroundColor: 'white',
                        '&:hover fieldset': {
                            borderColor: '#1dc39f',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: '#1dc39f',
                        },
                    },
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    backgroundColor: 'white',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#1dc39f',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#1dc39f',
                    },
                },
            },
        },
    },
});

const downloadColumns = [
    {
        id: 'kecamatan',
        label: 'Kecamatan',
    },
    {
        id: 'kelurahan',
        label: 'Kelurahan',
    },
    {
        id: 'nama_longwis',
        label: 'Nama Longwis',
    },
    {
        id: 'alamat',
        label: 'Alamat',
        minWidth: 150
    },
    {
        id: 'nama_pic',
        label: 'Nama PIC',
        minWidth: 100,
    },
    {
        id: 'kontak_pic',
        label: 'No Telpon',
        minWidth: 200,
    },
    {
        id: 'lokasi',
        label: 'Lokasi',
    },
    {
        id: 'serial_number',
        parent: 'InternetLongwi',
        label: 'Serial Number'
    }
];

const columns = [
    {
        id: 'kecamatan',
        label: 'Kecamatan',
        minWidth: 150
    },
    {
        id: 'kelurahan',
        label: 'Kelurahan',
        minWidth: 100,
    },
    {
        id: 'nama_longwis',
        label: 'Nama Longwis',
        minWidth: 200,
    },
    {
        id: 'alamat',
        label: 'Alamat',
        minWidth: 150,
    },
    {
        id: 'nama_pic',
        label: 'Nama PIC',
        minWidth: 150,
    },
    {
        id: 'kontak_pic',
        label: 'No Telpon',
        minWidth: 150,
    },
    {
        id: 'InternetLongwi',
        label: 'Serial Number ONT',
        minWidth: 150,
        format: (value) => {
            return(
                <Typography>
                    {value ? value.serial_number : "Belum Input"}
                </Typography>
            )
        }
    },
    {
        id: 'InternetLongwi',
        label: 'QR Code',
        minWidth: 150,
        format: (value) => {
            if(value && value.foto_qr){
                const filename = value.foto_qr.replace('.jpg', '');
                return (
                    <Box sx={{ textAlign: 'center' }}>
                        <img 
                            width={50} 
                            height={50}
                            alt={filename} 
                            src={process.env.REACT_APP_API_URL + "uploads/internet/"+filename}
                            style={{ borderRadius: 4, border: '1px solid #ddd' }}
                        />
                    </Box>
                );
            } else {
                return (
                    <Chip 
                        label="Tidak Ada" 
                        size="small" 
                        color="error" 
                        variant="outlined"
                    />
                );
            }
        }
    },
    {
        id: 'lokasi',
        label: 'Lokasi',
        minWidth: 150,
        format: (value) => {
            const coords = JSON.parse(value);
            return(
                <Typography>
                    {value ? <a href={'https://maps.google.com/maps?ll='+coords[0]+','+coords[1]+'&z=12&t=m&hl=en-US&gl=US&mapclient=embed&q=5%C2%B008%2750.6%22S%20119%C2%B025%2748.9%22E%20-5.147393%2C%20119.430255@'+coords[0]+','+coords[1]} target='_blank' rel="noreferrer">{"lat:"+coords[0]+", long:"+coords[1] }</a> : "Tidak Ada Lokasi"}
                </Typography>
            )
        }
    },
];


export default function LongwisInternet() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    //Page Number
    let rowNumber = 0;
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);
    //End of Page Number

    const [copyList, setCopyList] = useState(rows);

    //Filter
    const [kecamatanValue, setkecamatanValue] = useState('');
    const [kecamatanData, setkecamatanData] = useState([]);
    const [searchValue, setSearchValue] = useState('');
    //End of Filter

    const getDataListLongwis = useCallback(() => {
        setLoading(true);
        setError(null);
        
        fetch(process.env.REACT_APP_API_URL + "api/longwis", {
            method: 'get',
            headers: headerData
        })
            .then(res => {
                if (!res.ok) {
                    throw new Error('Gagal mengambil data longwis internet');
                }
                return res.json();
            })
            .then(data => {
                const filteredData = data.filter((item) => item.InternetLongwi !== null);
                setRows(filteredData);
            })
            .catch(err => {
                console.error('Error fetching longwis internet data:', err);
                setError(err.message || 'Terjadi kesalahan saat mengambil data');
            })
            .finally(() => {
                setLoading(false);
            });
    }, [])

    const searchText = (searched) => {
        setSearchValue(searched);
        let filteredData = rows;
        
        // Apply search filter
        if (searched) {
            filteredData = filteredData.filter((item) =>
                item.nama_longwis.toUpperCase().includes(searched.toUpperCase()) ||
                item.kecamatan.toUpperCase().includes(searched.toUpperCase()) ||
                item.kelurahan.toUpperCase().includes(searched.toUpperCase()) ||
                item.nama_pic.toUpperCase().includes(searched.toUpperCase()) ||
                (item.InternetLongwi && item.InternetLongwi.serial_number && 
                 item.InternetLongwi.serial_number.toUpperCase().includes(searched.toUpperCase()))
            );
        }
        
        // Apply kecamatan filter
        if (kecamatanValue) {
            filteredData = filteredData.filter((item) =>
                item.kecamatan.toUpperCase().trim().includes(kecamatanValue.toUpperCase().trim())
            );
        }
        
        setCopyList(filteredData);
    }

    //Page handle
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };
    //End of page Handle

    //Filter Hanlde
    const getkecamatanamatanData = useCallback(() => {
        let kecamatan_list = [];
        rows.forEach((items) => {
            if (kecamatan_list.includes(items.kecamatan)) {
                return
            } else {
                kecamatan_list.push(items.kecamatan);
            }
        })
        setkecamatanData(kecamatan_list);
    }, [rows, setkecamatanData])


    const kecamatanHandle = useCallback((e) => {
        setkecamatanValue(e);
        let filteredData = rows;
        
        // Apply kecamatan filter
        if (e) {
            filteredData = filteredData.filter((item) =>
                item.kecamatan.toUpperCase().trim().includes(e.toUpperCase().trim())
            );
        }
        
        // Apply search filter if exists
        if (searchValue) {
            filteredData = filteredData.filter((item) =>
                item.nama_longwis.toUpperCase().includes(searchValue.toUpperCase()) ||
                item.kecamatan.toUpperCase().includes(searchValue.toUpperCase()) ||
                item.kelurahan.toUpperCase().includes(searchValue.toUpperCase()) ||
                item.nama_pic.toUpperCase().includes(searchValue.toUpperCase()) ||
                (item.InternetLongwi && item.InternetLongwi.serial_number && 
                 item.InternetLongwi.serial_number.toUpperCase().includes(searchValue.toUpperCase()))
            );
        }
        
        setCopyList(filteredData);
    }, [rows, searchValue]);

    const clearFilters = () => {
        setSearchValue('');
        setkecamatanValue('');
        setCopyList(rows);
    };

    //End Of Filter handle

    useEffect(() => {
        if (rows.length === 0) {
            getDataListLongwis()
        } else {
            setCopyList(rows)
            getkecamatanamatanData()
        }
    },
        [getDataListLongwis, rows, getkecamatanamatanData]
    )

    return (
        <Box
            sx={{
                minHeight: "100vh",
                background: "linear-gradient(135deg, #e8f5e8 0%, #f0fff0 100%)",
                py: 4
            }}
        >
            <ThemeProvider theme={defaultTheme}>
                <Container maxWidth="xl">
                    {/* Header Section */}
                    <Paper 
                        elevation={8}
                        sx={{
                            p: 4,
                            mb: 3,
                            borderRadius: 3,
                            background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.8) 100%)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255,255,255,0.2)'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                            <Box
                                sx={{
                                    width: 70,
                                    height: 70,
                                    borderRadius: '50%',
                                    backgroundColor: 'primary.main',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mr: 3,
                                    boxShadow: 3
                                }}
                            >
                                <WifiIcon sx={{ fontSize: 35, color: 'white' }} />
                            </Box>
                            
                            <Box sx={{ flex: 1 }}>
                                <Typography 
                                    component="h1" 
                                    variant="h4" 
                                    sx={{ 
                                        color: 'primary.main', 
                                        fontWeight: 700,
                                        mb: 0.5
                                    }}
                                >
                                    Data Internet Longwis
                                </Typography>
                                <Typography 
                                    variant="body1" 
                                    sx={{ 
                                        color: 'text.secondary',
                                        fontWeight: 500
                                    }}
                                >
                                    Kelola data internet dan ONT lorong wisata
                                </Typography>
                            </Box>

                            {/* Stats Cards */}
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Card sx={{ minWidth: 120, textAlign: 'center' }}>
                                    <CardContent sx={{ py: 2 }}>
                                        <Typography variant="h6" color="primary.main" fontWeight={700}>
                                            {copyList.length}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Total Internet
                                        </Typography>
                                    </CardContent>
                                </Card>
                                <Card sx={{ minWidth: 120, textAlign: 'center' }}>
                                    <CardContent sx={{ py: 2 }}>
                                        <Typography variant="h6" color="success.main" fontWeight={700}>
                                            {rows.filter(item => 
                                                item.InternetLongwi && item.InternetLongwi.serial_number
                                            ).length}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Ada Serial
                                        </Typography>
                                    </CardContent>
                                </Card>
                                <Card sx={{ minWidth: 120, textAlign: 'center' }}>
                                    <CardContent sx={{ py: 2 }}>
                                        <Typography variant="h6" color="warning.main" fontWeight={700}>
                                            {rows.filter(item => 
                                                item.InternetLongwi && item.InternetLongwi.foto_qr
                                            ).length}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Ada QR Code
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Box>
                        </Box>

                        {/* Error Alert */}
                        {error && (
                            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                                {error}
                            </Alert>
                        )}

                        {/* Loading Alert */}
                        {loading && (
                            <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <CircularProgress size={20} />
                                    Memuat data internet longwis...
                                </Box>
                            </Alert>
                        )}
                    </Paper>

                    {/* Filters Section */}
                    <Paper 
                        elevation={4}
                        sx={{
                            p: 3,
                            mb: 3,
                            borderRadius: 3,
                            background: 'white'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                            <FilterIcon color="primary" sx={{ mr: 1 }} />
                            <Typography variant="h6" fontWeight={600}>
                                Filter & Pencarian
                            </Typography>
                        </Box>

                        <Grid container spacing={3} alignItems="center">
                            {/* Search */}
                            <Grid item xs={12} md={5}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Pencarian"
                                    placeholder="Nama longwis, kecamatan, kelurahan, PIC, atau serial number"
                                    value={searchValue}
                                    onChange={(e) => searchText(e.target.value)}
                                    InputProps={{
                                        startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                                    }}
                                />
                            </Grid>

                            {/* Kecamatan Filter */}
                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Kecamatan</InputLabel>
                                    <Select
                                        value={kecamatanValue}
                                        label="Kecamatan"
                                        onChange={(e) => kecamatanHandle(e.target.value)}
                                        startAdornment={<BusinessIcon color="action" sx={{ mr: 1 }} />}
                                    >
                                        <MenuItem value="">
                                            <em>Semua Kecamatan</em>
                                        </MenuItem>
                                        {kecamatanData.map((items, index) => (
                                            <MenuItem key={index} value={items}>{items}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Actions */}
                            <Grid item xs={12} md={3}>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                        variant="outlined"
                                        onClick={clearFilters}
                                        size="small"
                                        sx={{ px: 2 }}
                                    >
                                        Reset
                                    </Button>
                                    <DownloadTable 
                                        columns={downloadColumns} 
                                        rows={copyList} 
                                        filename={'data_LongwisInternet.csv'} 
                                    />
                                </Box>
                            </Grid>
                        </Grid>

                        {/* Active Filters */}
                        {(searchValue || kecamatanValue) && (
                            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                                    Filter aktif:
                                </Typography>
                                {searchValue && (
                                    <Chip
                                        size="small"
                                        label={`Pencarian: ${searchValue}`}
                                        onDelete={() => {
                                            setSearchValue('');
                                            searchText('');
                                        }}
                                        color="primary"
                                        variant="outlined"
                                    />
                                )}
                                {kecamatanValue && (
                                    <Chip
                                        size="small"
                                        label={`Kecamatan: ${kecamatanValue}`}
                                        onDelete={() => kecamatanHandle('')}
                                        color="primary"
                                        variant="outlined"
                                    />
                                )}
                            </Box>
                        )}
                    </Paper>

                    {/* Table Section */}
                    <Paper 
                        elevation={8}
                        sx={{
                            borderRadius: 3,
                            overflow: 'hidden',
                            background: 'white'
                        }}
                    >
                        <TableContainer sx={{ maxHeight: '70vh' }}>
                            <Table size='small' stickyHeader aria-label="sticky table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                backgroundColor: 'primary.main',
                                                color: 'white',
                                                fontWeight: 700,
                                                minWidth: 60
                                            }}
                                        >
                                            No
                                        </TableCell>
                                        {columns.map((column, index) => (
                                            <TableCell
                                                key={`${column.id}-${index}`}
                                                align={column.align || 'left'}
                                                sx={{
                                                    backgroundColor: 'primary.main',
                                                    color: 'white',
                                                    fontWeight: 700,
                                                    minWidth: column.minWidth
                                                }}
                                            >
                                                {column.label}
                                            </TableCell>
                                        ))}
                                        <TableCell
                                            align="center"
                                            sx={{
                                                backgroundColor: 'primary.main',
                                                color: 'white',
                                                fontWeight: 700,
                                                minWidth: 180
                                            }}
                                        >
                                            Aksi
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {copyList
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((row, index) => {
                                            const currentRowNumber = index + 1 + page * rowsPerPage;
                                            return (
                                                <TableRow 
                                                    hover 
                                                    role="checkbox" 
                                                    tabIndex={-1} 
                                                    key={row.id}
                                                    sx={{
                                                        '&:nth-of-type(even)': {
                                                            backgroundColor: 'rgba(29, 195, 159, 0.04)'
                                                        }
                                                    }}
                                                >
                                                    <TableCell align="center">
                                                        <Chip 
                                                            label={currentRowNumber} 
                                                            size="small" 
                                                            color="primary" 
                                                            variant="outlined"
                                                        />
                                                    </TableCell>
                                                    {columns.map((column, colIndex) => {
                                                        const value = row[column.id];
                                                        return (
                                                            <TableCell 
                                                                key={`${column.id}-${colIndex}`} 
                                                                align={column.align || 'left'}
                                                            >
                                                                {column.format ? column.format(value) : value}
                                                            </TableCell>
                                                        );
                                                    })}
                                                    <TableCell align="center">
                                                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                                            <Button
                                                                variant="contained"
                                                                onClick={() => !row.lokasi ? 
                                                                    window.open("/#/download/"+row.id, "_blank") : 
                                                                    window.open("/#/download/internet/"+row.id, "_blank")
                                                                }
                                                                sx={{
                                                                    backgroundColor: "success.main",
                                                                    textTransform: "none",
                                                                    fontWeight: 500,
                                                                    minWidth: 'auto',
                                                                    px: 2,
                                                                    "&:hover": {
                                                                        backgroundColor: "success.dark",
                                                                    },
                                                                }}
                                                                size="small"
                                                                title="Download Laporan Internet"
                                                            >
                                                                <DownloadRounded />
                                                            </Button>
                                                            <DeleteData id={row.id} execute={getDataListLongwis} />
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            component="div"
                            count={copyList.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            sx={{
                                borderTop: '1px solid',
                                borderColor: 'divider',
                                backgroundColor: 'grey.50'
                            }}
                        />
                    </Paper>
                </Container>
            </ThemeProvider>
        </Box>
    );
}